/**
 * Login Form Component
 *
 * A form for user authentication with email and password.
 * Includes validation, error handling, and navigation.
 *
 * @fileoverview Login form with Supabase authentication
 *
 * @example
 * <LoginForm onSuccess={() => navigate('/home')} />
 */

import React, { useState, FormEvent, useCallback } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Link as ChakraLink,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { validateEmail } from '../../utils/validation';
import AuthLayout from './AuthLayout';
import { supabase } from '../../lib/supabase';
import { apiJson } from '../../lib/api';

/**
 * Props for LoginForm component
 */
interface LoginFormProps {
  /** Callback function called on successful login */
  onSuccess?: () => void;
}

/**
 * Login Form Component
 *
 * Handles user sign-in with email and password.
 * Features:
 * - Email and password validation
 * - Password visibility toggle
 * - Error message display
 * - Loading state during submission
 * - Links to sign up and password recovery
 *
 * @param props - Component props
 * @returns Login form component
 *
 * @example
 * function LoginPage() {
 *   const navigate = useNavigate();
 *   return <LoginForm onSuccess={() => navigate('/home')} />;
 * }
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading, error, clearError } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Validation state
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [touched, setTouched] = useState({ email: false, password: false });

  // Color mode values
  const linkColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');

  /**
   * Validate form fields
   */
  const validateForm = useCallback((): boolean => {
    const emailValidation = validateEmail(email);
    const passwordValidation = password ? null : 'Password is required';

    setEmailError(emailValidation);
    setPasswordError(passwordValidation);

    return !emailValidation && !passwordValidation;
  }, [email, password]);

  /**
   * Handle email input blur for validation
   */
  const handleEmailBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, email: true }));
    setEmailError(validateEmail(email));
  }, [email]);

  /**
   * Handle password input blur for validation
   */
  const handlePasswordBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, password: true }));
    setPasswordError(password ? null : 'Password is required');
  }, [password]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      clearError();

      // Mark all fields as touched
      setTouched({ email: true, password: true });

      // Validate form
      if (!validateForm()) {
        return;
      }

      // Attempt sign in
      const result = await signIn(email, password);

      if (result.success) {
        if (onSuccess) {
          onSuccess();
        } else {
          const from = (location.state as any)?.from?.pathname;
          if (from) {
            navigate(from, { replace: true });
          } else {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const profile = session?.access_token
                ? await apiJson<{ globalRole: string }>('/api/auth/me', {}, session.access_token)
                : null;
              navigate('/home', { replace: true });
            } catch {
              navigate('/home', { replace: true });
            }
          }
        }
      }
    },
    [email, password, signIn, validateForm, clearError, onSuccess, navigate, location]
  );

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <AuthLayout
      title="Welcome Back"
      subtitle="Sign in to your account to continue"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={5}>
          {/* Error Alert */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Email Field */}
          <FormControl isInvalid={touched.email && !!emailError}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              autoComplete="email"
              size="lg"
            />
            <FormErrorMessage>{emailError}</FormErrorMessage>
          </FormControl>

          {/* Password Field */}
          <FormControl isInvalid={touched.password && !!passwordError}>
            <FormLabel>Password</FormLabel>
            <InputGroup size="lg">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={handlePasswordBlur}
                autoComplete="current-password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={togglePasswordVisibility}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{passwordError}</FormErrorMessage>
          </FormControl>

          {/* Forgot Password Link */}
          <HStack w="full" justify="flex-end">
            <ChakraLink
              as={RouterLink}
              to="/forgot-password"
              color={linkColor}
              fontSize="sm"
              fontWeight="medium"
            >
              Forgot password?
            </ChakraLink>
          </HStack>

          {/* Submit Button */}
          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            w="full"
            isLoading={loading}
            loadingText="Signing in..."
          >
            Sign In
          </Button>

          {/* Divider */}
          <HStack w="full">
            <Divider />
            <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
              or
            </Text>
            <Divider />
          </HStack>

          {/* Sign Up Link */}
          <Text fontSize="sm" textAlign="center">
            Don't have an account?{' '}
            <ChakraLink
              as={RouterLink}
              to="/signup"
              color={linkColor}
              fontWeight="medium"
            >
              Sign up
            </ChakraLink>
          </Text>
        </VStack>
      </form>
    </AuthLayout>
  );
};

export default LoginForm;
